/*
 * Copyright (c) 2026.  atagon GmbH.  All rights reserved.
 *
 * This file is part of the atagon codebase and may not be copied,
 *  distributed, or modified without explicit written permission from
 * atagon GmbH. https://www.atagon.com
 */

import React, {useEffect, useState} from 'react';
import {AnimatePresence, motion} from 'motion/react';
import {Database} from "../APIClient.jsx";
import {Nodes} from "../Objects/Nodes.jsx";
import {Containers} from "../Objects/Containers.jsx";
import {Socket} from "../Socket.js";
import {useSub} from "subscription";
import {UI} from "../UIGenerator.jsx";
import Algo from "toolbox/algo";
import ElementStatus from "./ElementStatus.jsx";
import TextInput from "./BasicInputs/TextInput.jsx";
import DisplayIcon from "./Icons/DisplayIcon.jsx";


const StartGrid = () => {
    const [containers, setContainers] = useState([]);
    const [services, setServices] = useState([]);
    const [pages, setPages] = useState([]);
    const [nodes, setNodes] = useState([]);
    const [searchInput, setSearchInput] = useState("");
    const [activeFilter, setActiveFilter] = useState("All");
    const [isUpdatingClients, setIsUpdatingClients] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                let containersData = await Containers().listAll();
                let servicesData = await Database.table("Services", "app").getData();
                let pagesData = await Database.table("Pages", "app").getData();
                let nodesData = await Nodes().listAll();

                setContainers(containersData?.recordset || containersData || []);
                setServices(servicesData.recordset || []);
                setPages(pagesData.recordset || []);
                setNodes(nodesData?.recordset || nodesData || []);
            } catch (err) {
                console.error("Error fetching dashboard data:", err);
            }
        };
        fetchData();
    }, []);

    useSub(() => Database.table("Container", "app").onChange((change) => {
        setContainers(prev => Socket.applyChange(prev, change))
    }, []), "liveContainerData");

    useSub(() => Database.table("Services", "app").onChange((change) => {
        setServices(prev => Socket.applyChange(prev, change))
    }, []), "liveServicesData");

    useSub(() => Database.table("Pages", "app").onChange((change) => {
        setPages(prev => Socket.applyChange(prev, change))
    }, []), "livePagesData");

    useSub(() => Database.table("Nodes", "app").onChange((change) => {
        setNodes(prev => Socket.applyChange(prev, change))
    }, []), "liveNodesData");

    const handleUpdateAllEdgeClients = async () => {
        setIsUpdatingClients(true);
        try {
            await Promise.all(nodes.map(node => {
                return Nodes(node.indexKey).updateClient();
            }));
        } catch (err) {
            console.error("Error updating edge clients:", err);
        } finally {
            setIsUpdatingClients(false);
        }
    };


    const getVisibleElements = () => {
        const normServices = services.map(s => ({
            ...s,
            type: 'Services',
            name: s.serviceName,
            path: "/s/" + s.indexKey,
            icon: "Container",
            image: s.image || null
        }));

        const normContainers = containers.map(c => ({
            ...c,
            type: 'Container',
            name: c.containerName,
            path: "/c/" + c.indexKey,
            icon: "Container",
            image: c.image || null
        }));

        const normPages = pages.map(p => ({
            ...p,
            type: 'Pages',
            name: p.pageName,
            path: "/p/" + p.indexKey,
            icon: "BsWindow",
            image: p.image || null
        }));

        let items = [];

        // Category Filter
        if (activeFilter === "All" || activeFilter === "Services") items.push(...normServices);
        if (activeFilter === "All" || activeFilter === "Container") items.push(...normContainers);
        if (activeFilter === "All" || activeFilter === "Pages") items.push(...normPages);

        if (!searchInput) return items;

        items = items.map(item => ({
            ...item,
            _score: Algo.levenshteinDistance(item.name, searchInput)
        }));

        items = items.sort((a, b) => a._score - b._score);

        return items;
    };

    const visibleElements = getVisibleElements();

    const FilterBadge = ({name, label}) => (
        <button
            onClick={() => setActiveFilter(name)}
            className={`px-3 py-1 text-sm font-medium rounded-full transition-all
                ${activeFilter === name
                ? "bg-zinc-800 text-white hover:bg-zinc-700"
                : "bg-zinc-50 text-gray-600 hover:bg-zinc-200"
            }`}
        >
            {label}
        </button>
    );

    return (
        <div className="flex flex-col h-full w-full">

            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                <div className="relative w-full md:w-96 group">
                    <TextInput hasSearch={true}
                               className={"w-full! min-w-full! max-w-full! bg-white"}
                               value={searchInput}
                               onChange={setSearchInput}
                               hasClose={false}
                               placeholder={`Search Elements`}
                    />
                </div>

                <div className="flex flex-wrap gap-2 self-end md:self-auto">
                    <button
                        onClick={handleUpdateAllEdgeClients}
                        disabled={isUpdatingClients || nodes.length === 0}
                        className="px-3 py-1 text-sm font-medium rounded-full transition-all bg-blue-50 text-blue-600 hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isUpdatingClients ? 'Updating...' : 'Update Edge Clients'}
                    </button>
                    <FilterBadge name="All" label="All Apps"/>
                    <FilterBadge name="Services" label="Services"/>
                    <FilterBadge name="Container" label="Containers"/>
                    <FilterBadge name="Pages" label="Pages"/>
                </div>
            </div>

            {/* --- Grid Content --- */}
            <motion.div
                layout
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 pb-10"
            >
                <AnimatePresence mode='popLayout'>
                    {visibleElements.map((element) => {

                        return <motion.div
                            layout={"position"}
                            key={element.indexKey}
                            layoutId={element.indexKey}
                            initial={{opacity: 0, scale: 0.9}}
                            animate={{opacity: 1, scale: 1}}
                            exit={{opacity: 0, scale: 0.9}}
                            transition={{type: "spring", stiffness: 80, damping: 15}}
                            onClick={() => {
                                UI.page().navigate({pathname: element.path})
                            }}
                            className="group relative bg-white rounded-2xl border border-gray-100 cursor-pointer overflow-hidden h-52 shadow-sm "
                        >
                            <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-gray-100 to-gray-200">
                                {element.backgroundURL ? (
                                    <motion.img
                                        // Changed: h-full and object-cover to fill the whole card
                                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 shadow-inner"
                                        whileHover={{scale: 1.1, rotate: 1}}
                                        transition={{
                                            type: "spring",
                                            stiffness: 450,
                                            damping: 15
                                        }}
                                        src={`/images/containerImages/${element?.backgroundURL}.jpg`}
                                        alt=""/>
                                ) : (
                                    <div
                                        className="w-full h-full flex items-center justify-center text-gray-300 bg-gray-50">
                                        <DisplayIcon iconName={"Container"} className={"size-16!"}/>

                                    </div>
                                )}
                            </div>

                            <div className="absolute top-3 right-3 z-10 shadow-xl">
                                <ElementStatus name={element.type} elementId={element.indexKey}/>
                            </div>

                            <div
                                className="absolute bottom-0 w-full p-4 bg-white/80  border-t border-white/50 flex items-center justify-between transition-colors">
                                <div className="min-w-0 flex-1 pr-2">
                                    <h3 className="text-lg font-bold text-gray-900 group-hover:text-blue-600 transition-colors truncate"
                                        title={element.name}>
                                        {element.name}
                                    </h3>
                                </div>
                                <div
                                    className="flex-shrink-0 text-gray-400 group-hover:text-blue-600 transition-colors group-hover:translate-x-1 duration-300">
                                    <DisplayIcon iconName={"PiCaretRight"}/>
                                </div>
                            </div>
                        </motion.div>
                    })}
                </AnimatePresence>

                <motion.div
                    layout={"position"}
                    onClick={() => UI.page().navigate({pathname: "/new"})}
                    className="group bg-white rounded-2xl border-2 border-dashed border-gray-300 hover:border-blue-400 hover:bg-blue-50 transition-all cursor-pointer flex flex-col items-center justify-center text-gray-400 hover:text-blue-500 h-52"
                >
                    <div
                        className="w-16 h-16 rounded-xl   flex items-center justify-center mb-4 ">
                        <DisplayIcon iconName={"PiPlus"} className="w-8 h-8 text-blue-500"/>

                    </div>
                    <span className=" text-md">New Deployment</span>
                </motion.div>

            </motion.div>
        </div>
    );
}

export default StartGrid;